import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AlertSystem.css';

interface Alert {
  id: string;
  type: string;
  message: string;
  timestamp: string;
  severity: 'info' | 'warning' | 'error';
}

interface AlertSystemProps {
  alerts: Alert[];
  onDismiss?: (alertId: string) => void;
}

const AlertSystem: React.FC<AlertSystemProps> = ({ alerts, onDismiss }) => {
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'warning': return '⚠️';
      case 'error': return '🚨';
      default: return 'ℹ️';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'warning': return '#FF2D55';
      case 'error': return '#FF2D55';
      default: return '#1A73E8';
    }
  };

  const handleDismiss = (alertId: string) => {
    if (onDismiss) {
      onDismiss(alertId);
    }
  };

  if (alerts.length === 0) return null;

  return (
    <div className="alert-system">
      <AnimatePresence>
        {alerts.map((alert) => (
          <motion.div
            key={alert.id}
            className={`alert-item ${alert.severity}`}
            initial={{ opacity: 0, x: 300, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.8 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300
            }}
            style={{
              borderLeftColor: getSeverityColor(alert.severity)
            }}
          >
            <div className="alert-icon">
              {getSeverityIcon(alert.severity)}
            </div>
            
            <div className="alert-content">
              <div className="alert-message">{alert.message}</div>
              <div className="alert-timestamp">
                {new Date(alert.timestamp).toLocaleTimeString()}
              </div>
            </div>
            
            <motion.button
              className="alert-dismiss"
              onClick={() => handleDismiss(alert.id)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              ✕
            </motion.button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default AlertSystem;
